import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// The points are the number in "(n)" at the start of the answer: 0, 12 or 25.
// The 0-2 regex of Mini-BESTest and Fugl-Meyer cannot read (12) or (25).
const TRUNK_CONTROL_TEST_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const TRUNK_CONTROL_TEST_ALLOWED_POINTS = [0, 12, 25];

// Titles separate the instruction with "||" or "‖".
const TRUNK_CONTROL_TEST_TITLE_SEPARATOR = /\|\||‖/;

const TRUNK_CONTROL_TEST_ITEMS = [
  'question802',
  'question803',
  'question804',
  'question805',
];
const TRUNK_CONTROL_TEST_TOTAL_CONTROL_NAME = 'question806';
const TRUNK_CONTROL_TEST_TOTAL_MAX_SCORE = 100;

@Component({
  selector: 'app-trunk-control-test-questions',
  templateUrl: './trunk-control-test-questions.component.html',
  styleUrl: './trunk-control-test-questions.component.css',
  standalone: false,
})
export class TrunkControlTestQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(TRUNK_CONTROL_TEST_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(TRUNK_CONTROL_TEST_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.name === TRUNK_CONTROL_TEST_TOTAL_CONTROL_NAME;
  }

  get maxScore(): number {
    return TRUNK_CONTROL_TEST_TOTAL_MAX_SCORE;
  }

  getScoreDisplay(): string {
    const value = this.formGroup.get(
      TRUNK_CONTROL_TEST_TOTAL_CONTROL_NAME,
    )?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  // Live total of the items answered so far, or empty while none is, so an
  // untouched test is not saved.
  private recalculateTotal(): void {
    const points = TRUNK_CONTROL_TEST_ITEMS.map((controlName) =>
      this.getSelectedPoints(controlName),
    ).filter((point): point is number => point !== null);
    const total = points.length
      ? points.reduce((sum, point) => sum + point, 0)
      : null;

    const totalCtrl = this.formGroup.get(TRUNK_CONTROL_TEST_TOTAL_CONTROL_NAME);
    if (totalCtrl) {
      totalCtrl.setValue(total === null ? '' : String(total), {
        emitEvent: false,
      });
      totalCtrl.disable({ emitEvent: false });
    }

    this.cdr.detectChanges();
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(TRUNK_CONTROL_TEST_POINTS_REGEX);
    const points = match ? Number(match[1]) : null;

    return points !== null && TRUNK_CONTROL_TEST_ALLOWED_POINTS.includes(points)
      ? points
      : null;
  }
}
